import { useLingui } from "@lingui/react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useDeleteActivity } from "@/hooks/useDeleteActivity";
import { formatDateTime } from "@/lib/format";
import { getActivityTypeLabels, type ActivityResponse } from "@/types/activities";

interface DeleteActivityDialogProps {
  activity: ActivityResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Delete Activity Confirmation Dialog
 *
 * Displays activity summary and confirms deletion. Closes only when the delete
 * succeeds; on failure it stays open (the hook toasts the reason) so the user
 * can retry or cancel.
 */
export function DeleteActivityDialog({
  activity,
  open,
  onOpenChange,
  onSuccess,
}: DeleteActivityDialogProps) {
  const { i18n } = useLingui();
  const deleteMutation = useDeleteActivity();

  // The dialog is only rendered for a persisted activity, so `id` is set; the
  // generated ActivityResponse marks every field optional.
  const handleDelete = () =>
    deleteMutation.mutateAsync(activity.id!, {
      onSuccess: () => {
        onSuccess?.();
      },
    });

  // Format activity details for summary
  const childName = activity.child
    ? `${activity.child.firstname} ${activity.child.lastname}`
    : "-";
  const activityTypeLabels = getActivityTypeLabels(i18n);
  const activityType = activity.type ? activityTypeLabels[activity.type] : undefined;
  const activityTime = activity.activity_time
    ? formatDateTime(activity.activity_time, i18n.locale)
    : "-";

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._({ id: "activities.delete.title", message: "Delete Activity" })}
      description={i18n._({ id: "activities.delete.confirmMessage", message: "Are you sure you want to delete this activity? This action cannot be undone." })}
      confirmLabel={i18n._({ id: "activities.delete.delete", message: "Delete" })}
      cancelLabel={i18n._({ id: "activities.delete.cancel", message: "Cancel" })}
      variant="destructive"
      isPending={deleteMutation.isPending}
      onConfirm={handleDelete}
    >
      {/* Activity Summary */}
      <dl className="rounded-lg bg-muted p-4 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <dt className="font-medium text-muted-foreground">
            {i18n._({ id: "activities.delete.child", message: "Child" })}:
          </dt>
          <dd className="col-span-2">{childName}</dd>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <dt className="font-medium text-muted-foreground">
            {i18n._({ id: "activities.delete.type", message: "Type" })}:
          </dt>
          <dd className="col-span-2">{activityType}</dd>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <dt className="font-medium text-muted-foreground">
            {i18n._({ id: "activities.delete.time", message: "Time" })}:
          </dt>
          <dd className="col-span-2 tabular-nums">{activityTime}</dd>
        </div>
      </dl>
    </ConfirmDialog>
  );
}
